/**
 * Little Oak Total Pricing
 * Mirror of sprout-total-pricing.js, but for the Little Oak vendor.
 * Differences vs Sprout:
 *   - tiers use HARDCODED flat unit prices (not percent off base)
 *   - looks up table by `#little-oak-quantity-table`
 *   - vendor detection matches "little oak" instead of "sprout"
 */

class LittleOakTotalPricing {
  constructor() {
    // Hardcoded tiers: { quantity threshold, flat unit price in USD }
    // Matches the table in snippets/quantity-discounts-table-inline.liquid
    this.quantityBreaks = {
      default: 56.95, // base unit price (1-5)
      tiers: [
        { quantity: 6,  price: 54.99 }, // 6-11 pack
        { quantity: 12, price: 53.99 }  // 12+ pack
      ]
    };

    this.totalPriceContainer = null;
    this.quantityBreaksTable = null;
    this.currentVariant = null;
    this.variantChangeUnsubscriber = null;
    this.init();
  }

  init() {
    try {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    } catch (error) {
      // silent
    }
  }

  isLittleOakProduct() {
    const currentUrl = window.location.pathname.toLowerCase();
    const productTitle = document.querySelector('.product__title, h1')?.textContent?.toLowerCase() || '';
    const productHandle = document.querySelector('[data-product-handle]')?.getAttribute('data-product-handle')?.toLowerCase() || '';
    const metaTitle = document.querySelector('meta[property="og:title"]')?.content?.toLowerCase() || '';
    const pageTitle = document.title.toLowerCase();
    // Look for the unique inline table the Liquid snippet emits for Little Oak.
    const hasLOTable = !!document.querySelector('#little-oak-quantity-table');

    return (
      hasLOTable ||
      currentUrl.includes('little-oak') ||
      productTitle.includes('little oak') ||
      productTitle.includes('littleoak') ||
      pageTitle.includes('little oak') ||
      pageTitle.includes('littleoak') ||
      metaTitle.includes('little oak') ||
      metaTitle.includes('littleoak') ||
      productHandle.includes('little-oak')
    );
  }

  setup() {
    const isCartContext =
      document.body.classList.contains('template-cart') || window.location.pathname.startsWith('/cart');
    if (isCartContext) return;

    if (!this.isLittleOakProduct()) return;

    this.quantityBreaksTable = document.querySelector('#little-oak-quantity-table .quantity-discounts-table');
    if (!this.quantityBreaksTable) return;
    if (this.quantityBreaksTable.closest('cart-drawer')) return;

    this.createTotalPriceDisplay();
    this.setupEventListeners();
    this.updatePricing();
  }

  createTotalPriceDisplay() {
    const contextRoot =
      (this.quantityBreaksTable &&
        this.quantityBreaksTable.closest('.product, .product__info-wrapper, .product__info-container, form')) ||
      document;

    const host = contextRoot.querySelector('[data-total-price-host]');
    const hostIsContainer = host && host.classList.contains('sprout-total-price');
    let totalPriceContainer = hostIsContainer
      ? host
      : contextRoot.querySelector('.sprout-total-price.total-price.current-total');

    if (!totalPriceContainer) {
      totalPriceContainer = document.createElement('div');
      totalPriceContainer.className = 'sprout-total-price total-price current-total';
    }
    totalPriceContainer.style = 'font-size: 1.4rem; font-weight: 600; text-align: left; color: #333;';

    if (host) {
      if (!hostIsContainer) {
        host.innerHTML = '';
        host.appendChild(totalPriceContainer);
      }
      this.totalPriceContainer = totalPriceContainer;
      return;
    }

    const quantityWrapper =
      contextRoot.querySelector('.product-form__input.product-form__quantity') ||
      contextRoot.querySelector('.product-form__quantity');
    if (!quantityWrapper) return;

    const quantityInputEl =
      quantityWrapper.querySelector('quantity-input, input[name="quantity"], .quantity__input') ||
      contextRoot.querySelector('.product-form__quantity quantity-input, input[name="quantity"], .quantity__input');

    if (quantityInputEl && quantityInputEl.parentNode) {
      quantityInputEl.insertAdjacentElement('afterend', totalPriceContainer);
    } else {
      quantityWrapper.appendChild(totalPriceContainer);
    }

    this.totalPriceContainer = totalPriceContainer;
  }

  setupEventListeners() {
    const debouncedUpdate = this.debounce(() => this.updatePricing(), 300);

    document.addEventListener('input', (e) => {
      if (e.target.matches('input[name="quantity"], .quantity__input, input[type="number"][id*="Quantity"]')) {
        debouncedUpdate();
      }
    });
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="quantity"], .quantity__input, input[type="number"][id*="Quantity"]')) {
        this.updatePricing();
      }
    });
    document.addEventListener('variant:change', () => {
      setTimeout(() => this.updatePricing(), 50);
    });
    document.addEventListener('click', (e) => {
      if (
        e.target.matches('.quantity__button') ||
        e.target.closest('.quantity__button') ||
        e.target.matches('[name="plus"], [name="minus"]') ||
        e.target.closest('[name="plus"], [name="minus"]')
      ) {
        setTimeout(() => this.updatePricing(), 50);
      }
    });
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  updatePricing() {
    const quantity = this.getCurrentQuantity();
    if (quantity < 1) return;

    const { unitPrice, activeRowIndex, tier } = this.calculatePricing(quantity);
    this.highlightActiveRow(activeRowIndex);
    this.updateTotalPriceDisplay(quantity, unitPrice, tier);
    this.updateTablePrices(activeRowIndex);
  }

  getCurrentQuantity() {
    const table = this.quantityBreaksTable;
    let container = document;
    if (table) container = table.closest('form, .product-form, .product') || document;
    const input =
      (container.querySelector && container.querySelector('input[name="quantity"], .quantity__input')) ||
      document.querySelector('input[name="quantity"], .quantity__input');
    return input ? parseInt(input.value, 10) || 1 : 1;
  }

  calculatePricing(quantity) {
    let unitPrice = this.quantityBreaks.default;
    let activeRowIndex = 0;
    let tier = null;

    for (let i = this.quantityBreaks.tiers.length - 1; i >= 0; i--) {
      const currentTier = this.quantityBreaks.tiers[i];
      if (quantity >= currentTier.quantity) {
        unitPrice = currentTier.price;
        activeRowIndex = i + 1; // +1 because base price is row 0
        tier = currentTier;
        break;
      }
    }
    return { unitPrice, activeRowIndex, tier };
  }

  highlightActiveRow(activeRowIndex) {
    if (!this.quantityBreaksTable) return;
    const rows = this.quantityBreaksTable.querySelectorAll('tbody tr');
    rows.forEach((row) => {
      row.classList.remove('orichi-table__row--active', 'orichi-active-row');
    });
    if (rows[activeRowIndex]) {
      rows[activeRowIndex].classList.add('orichi-table__row--active', 'orichi-active-row');
    }
  }

  updateTablePrices(activeRowIndex) {
    if (!this.quantityBreaksTable) return;
    const rows = this.quantityBreaksTable.querySelectorAll('tbody tr');

    // Strikethrough all unit-price cells above the active tier (Sprout pattern)
    rows.forEach((row, rowIndex) => {
      const priceCell = row.querySelector('.unit-price-cell');
      if (!priceCell) return;
      priceCell.style.textDecoration = 'none';
      priceCell.style.color = '';
      if (rowIndex < activeRowIndex) {
        priceCell.style.textDecoration = 'line-through';
        priceCell.style.color = '#999';
      }
    });
  }

  updateTotalPriceDisplay(quantity, unitPrice, tier) {
    if (!this.totalPriceContainer) return;
    const totalPrice = quantity * unitPrice;
    const formatPrice = (price) => `$${price.toFixed(2)}`;

    let originalTotalHTML = '';
    let savingsHTML = '';

    if (tier) {
      const originalPrice = quantity * this.quantityBreaks.default;
      if (originalPrice > totalPrice && totalPrice > 0) {
        // Strikethrough original price inline with the total
        originalTotalHTML = ` <span class="original-total" style="text-decoration: line-through; color: #999; margin-left: 0.5rem;">${formatPrice(originalPrice)}</span>`;

        // "You Saved: $X.XX" line below — mirrors global.js's Sprout pattern
        const savings = originalPrice - totalPrice;
        if (savings > 0) {
          savingsHTML = `<div class="savings-display" style="font-size: 1.2rem; font-weight: 600; color: #428426; margin-top: 0.4rem;">You Saved: ${formatPrice(savings)}</div>`;
        }
      }
    }

    this.totalPriceContainer.innerHTML = `${formatPrice(totalPrice)}${originalTotalHTML}${savingsHTML}`;

    document.dispatchEvent(
      new CustomEvent('little-oak:pricing:updated', {
        detail: { quantity, unitPrice, totalPrice, tier }
      })
    );
  }
}

if (typeof window !== 'undefined') {
  window.LittleOakTotalPricing = LittleOakTotalPricing;
  window.littleOakTotalPricingInstance = new LittleOakTotalPricing();
}
